import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { focusableWithin, useFocusTrap } from '../lib/use-focus-trap.js';
import Notice from './Notice.jsx';

// The one modal dialog of the portal (ENTRAID-347 redesign, brand/onguard/README.md "Dialogs"). Every modal uses it,
// so they all look and behave the same:
// - the backdrop is rendered in a portal on document.body, above every layer of the shell (top bar, menus, sticky
//   bars), so it always covers the whole viewport, top bar included, whatever stacking contexts the page creates;
// - the page behind does not scroll while the dialog is open (no scroll chaining, no overscroll strip at the top);
// - the panel is the card surface (white, 1 px line, 16 px radius) with a soft elevation, a Montserrat ExtraBold navy
//   title, 14 px ink body and a footer row of buttons above a 1 px line;
// - an error inside a dialog is a bad Notice (DialogError), never raw red text.

/** Above the shell's popovers (z-40) and the navigation drawer (z-50). */
export const DIALOG_LAYER = 'z-[60]';

// Keeps the page behind an open dialog from scrolling; nested dialogs share one lock.
let locks = 0;
let saved = null;
function lockScroll() {
  const root = document.documentElement;
  if (locks === 0) {
    saved = { overflow: root.style.overflow, overscroll: root.style.overscrollBehavior };
    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';
  }
  locks += 1;
  return () => {
    locks -= 1;
    if (locks === 0 && saved) {
      root.style.overflow = saved.overflow;
      root.style.overscrollBehavior = saved.overscroll;
      saved = null;
    }
  };
}

/**
 * A modal dialog: traps focus, closes on Escape and on a backdrop click, returns focus to `returnFocusRef`.
 * Focus starts on the first control of the body (a field, a checkbox), not on the Close button;
 * a body with no control (a plain confirmation) starts on Close.
 * @param {{ open: boolean, title: import('react').ReactNode, description?: import('react').ReactNode,
 *   onClose: () => void, children?: import('react').ReactNode, footer?: import('react').ReactNode,
 *   returnFocusRef?: object|null, initialFocusRef?: object|null, wide?: boolean }} props
 */
export default function Dialog({ open, title, description = null, onClose, children, footer, returnFocusRef = null, initialFocusRef = null, wide = false }) {
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const autoFocusRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  useLayoutEffect(() => { autoFocusRef.current = focusableWithin(bodyRef.current)[0] ?? null; });
  useFocusTrap(panelRef, { active: open, onClose, returnFocusRef, initialFocusRef: initialFocusRef ?? autoFocusRef, closeOnOutsidePointer: false });
  useEffect(() => (open ? lockScroll() : undefined), [open]);
  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div
      className={`fixed inset-0 ${DIALOG_LAYER} flex items-center justify-center overscroll-contain p-4`}
      style={{ background: 'rgba(15, 36, 64, 0.55)' }}
      data-dialog-backdrop=""
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={`on-light flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-2xl border border-ipc-border bg-white text-ipc-ink shadow-2xl ${wide ? 'max-w-3xl' : 'max-w-xl'}`}
        data-dialog-panel=""
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div className="min-w-0">
            <h2 id={titleId} className="font-display text-2xl font-extrabold leading-tight tracking-tight text-ipc-primary">{title}</h2>
            {description ? <p id={descriptionId} className="mt-1.5 text-[15px] text-ipc-muted">{description}</p> : null}
          </div>
          <button type="button" aria-label="Close" className="-mr-2 -mt-1 shrink-0 rounded-md p-2 text-ipc-muted hover:bg-ipc-surface hover:text-ipc-ink" onClick={onClose}>
            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </button>
        </div>
        <div ref={bodyRef} className="mt-5 min-h-0 flex-1 space-y-5 overflow-y-auto px-6 pb-6 text-sm text-ipc-ink" data-dialog-body="">{children}</div>
        {footer ? <div className="flex flex-wrap justify-end gap-2.5 border-t border-ipc-border px-6 py-4" data-dialog-footer="">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/** The error of a dialog: a bad Notice (role="alert") with the message as its title. Renders nothing without one. */
export function DialogError({ message, className = '' }) {
  if (!message) return null;
  return <Notice tone="bad" titleAs="p" title={message} className={className} testId="dialog-error" />;
}
